import React, { useState, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../../components/ui/typography/Text';
import { AccentButton } from '../../../components/ui/buttons/AccentButton';
import { OutlineButton } from '../../../components/ui/buttons/OutlineButton';
import { Brain, BookOpen, Microscope } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

// Formula component for better styling
const FormulaDisplay: React.FC<{ formula: string }> = ({ formula }) => {
  return (
    <div className="my-2 p-2 bg-blue-50 rounded-md text-center">
      <p className="font-medium text-blue-900">{formula}</p>
    </div>
  );
};

// Fraction component for displaying mathematical fractions
const Fraction: React.FC<{ numerator: string; denominator: string }> = ({ numerator, denominator }) => {
  return (
    <div className="inline-block text-center mx-1 align-middle">
      <div className="px-1 py-0.5 text-gray-900 text-sm font-medium min-w-[180px]">{numerator}</div>
      <div className="border-t-2 border-gray-700 mx-auto w-full"></div>
      <div className="px-1 py-0.5 text-gray-900 text-sm font-medium min-w-[180px]">{denominator}</div>
    </div>
  );
};

// Scenario for the simulation
const taxiCabScenario = {
  title: "The Taxi Cab Problem",
  description: "A taxi was involved in a hit-and-run accident at night. Two taxi companies operate in the city: 85% of taxis are Green, and 15% are Blue. A witness identified the taxi as Blue. The court tested the witness's ability to identify taxi colors at night and found the witness was correct 80% of the time.",
  question: "What is the probability that the taxi involved in the accident was actually Blue?",
  options: [
    { id: 'a', text: "80%" },
    { id: 'b', text: "60%" },
    { id: 'c', text: "41%" },
    { id: 'd', text: "15%" }
  ],
  correctAnswer: 'c',
  explanation: "The correct answer is 41%.",
  baseRate: 0.15, // 15% of taxis are Blue
  witnessReliability: 0.8 // Witness is correct 80% of the time
};

/**
 * Base Rate Fallacy Simulation
 * 
 * This simulation demonstrates how we tend to ignore general statistical information (base rates)
 * and focus too much on specific details, leading to incorrect probability judgments.
 */
const BaseRateFallacySimulation: React.FC = () => {
  // ========== STATE MANAGEMENT ==========
  const [started, setStarted] = useState(false);
  const [stage, setStage] = useState<'intro' | 'task1' | 'task2' | 'results'>('intro');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  
  // Reference for scrolling
  const simulationRef = useRef<HTMLDivElement>(null);

  // ========== EVENT HANDLERS ==========
  // Handle user selecting an option
  const handleChoice = (optionId: string) => {
    setSelectedOption(optionId);
    
    // Move to results after selection
    setStage('results');
    setShowResults(true);
  };

  // Reset the simulation
  const handleReset = () => {
    setStarted(false);
    setStage('intro');
    setSelectedOption(null);
    setShowResults(false);
    
    // Scroll back to top of simulation
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ========== SIMULATION-SPECIFIC DATA & CALCULATIONS ==========
  // Colors for pie charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Calculate chart data
  const getChartData = () => {
    const { baseRate, witnessReliability } = taxiCabScenario;
    
    // Calculate the four possible outcomes
    const truePositives = baseRate * witnessReliability; // Blue taxi correctly identified
    const falseNegatives = baseRate * (1 - witnessReliability); // Blue taxi incorrectly identified as Green
    const falsePositives = (1 - baseRate) * (1 - witnessReliability); // Green taxi incorrectly identified as Blue
    const trueNegatives = (1 - baseRate) * witnessReliability; // Green taxi correctly identified
    
    // Calculate the probability of the taxi being Blue given the witness said Blue
    const probabilityGivenWitness = truePositives / (truePositives + falsePositives);
    
    return {
      taxiDistribution: [
        { name: 'Blue taxis', value: baseRate * 100, color: COLORS[0] },
        { name: 'Green taxis', value: (1 - baseRate) * 100, color: COLORS[1] }
      ],
      witnessOutcomes: [
        { name: 'Blue → Blue', desc: 'Blue correctly identified', value: truePositives * 100, color: COLORS[0] },
        { name: 'Green → Blue', desc: 'Green misidentified as Blue', value: falsePositives * 100, color: COLORS[1] },
        { name: 'Green → Green', desc: 'Green correctly identified', value: trueNegatives * 100, color: COLORS[2] },
        { name: 'Blue → Green', desc: 'Blue misidentified as Green', value: falseNegatives * 100, color: COLORS[3] }
      ],
      probabilityGivenWitness: probabilityGivenWitness * 100
    };
  };

  // Custom tooltip component with color square
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload[0]) return null;
    
    const data = payload[0].payload;
    const color = data.color;
    
    return (
      <div className="hidden sm:block bg-white px-2.5 py-1.5 border border-gray-200 rounded shadow-sm text-xs">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-4 h-4 rounded flex-shrink-0 border border-gray-200" style={{ backgroundColor: color }}></div>
          <span className="font-medium text-gray-800">{data.name}</span>
          <span className="ml-auto tabular-nums text-gray-700">{`${data.value.toFixed(1)}%`}</span>
        </div>
        {data.desc && (
          <p className="text-[10px] text-gray-500 pl-4">{data.desc}</p>
        )}
      </div>
    );
  };

  // Legend items for pie charts
  const getLegendItems = (data: any[], isWitnessOutcomes: boolean = false) => {
    return data.map((item, index) => {
      return (
        <div key={`legend-${index}`} className="flex items-start gap-1.5 py-0.5 last:mb-0">
          <span
            className="w-2.5 h-2.5 rounded-sm flex-shrink-0 mt-1"
            style={{ backgroundColor: COLORS[index % COLORS.length] }}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-1">
              <span className="text-[11px] sm:text-xs font-medium text-gray-700 truncate">
                {item.name}
              </span>
              <span className="text-[11px] sm:text-xs text-gray-700 flex-shrink-0 tabular-nums">
                {item.value.toFixed(1)}%
              </span>
            </div>
            {isWitnessOutcomes && item.desc && (
              <span className="text-[9px] sm:text-[10px] text-gray-500 line-clamp-2 leading-tight">
                {item.desc}
              </span>
            )}
          </div>
        </div>
      );
    });
  };

  // ========== COMPONENT CONTENT ==========
  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* TASK 1: Taxi Cab Problem */}
      <SimulationStep 
        title={taxiCabScenario.title}
        isActive={stage === 'task1'}
      >
        <div className="space-y-6">
          <Text className="text-gray-700">
            {taxiCabScenario.description}
          </Text>
          
          <Text className="text-gray-700 font-medium">
            {taxiCabScenario.question}
          </Text>
          
          <div className="grid grid-cols-1 gap-2 mt-4">
            {taxiCabScenario.options.map(option => (
              <OutlineButton
                key={option.id}
                onClick={() => handleChoice(option.id)}
                className={`py-3 px-4 ${selectedOption === option.id ? 'bg-orange-50 border-orange-300' : ''}`}
              >
                {option.text}
              </OutlineButton>
            ))}
          </div>
        </div>
      </SimulationStep>

      {/* RESULTS SECTION */}
      {showResults && (
        <div className="space-y-6">
          {/* Understanding Results Block */}
          <ResultBlock
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-8">
              {/* Step 1: User Results */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>
                
                <Text className="text-gray-700">
                  You selected <span className="font-medium text-orange-600">
                    {taxiCabScenario.options.find(o => o.id === selectedOption)?.text}
                  </span>.
                </Text>
                
                <ResultMessage 
                  isCorrect={selectedOption === taxiCabScenario.correctAnswer} 
                  message={
                    selectedOption === taxiCabScenario.correctAnswer
                      ? "Great job! You correctly applied Bayes' theorem and avoided the base rate fallacy."
                      : "This is a common response. The base rate fallacy causes us to focus on the witness's reliability (80%) while underweighting the fact that only 15% of taxis are Blue."
                  }
                />
                
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <Text className="text-gray-900 font-medium mb-2">
                    The correct answer is 41%. Using Bayes' theorem:
                  </Text>
                  <div className="bg-white rounded-lg border border-gray-200 text-center p-2 mb-2">
                    {/* Desktop version with fractions - hidden on mobile */}
                    <div className="hidden sm:block">
                      <div className="text-gray-900 text-sm font-medium mb-3 flex items-center justify-center">
                        <span className="mr-1">P(Blue|Witness) =</span> 
                        <Fraction 
                          numerator="P(Witness|Blue) × P(Blue)" 
                          denominator="P(Witness)" 
                        />
                      </div>
                      
                      <div className="text-gray-900 text-sm font-medium flex items-center justify-center">
                        <span className="mr-1">=</span>
                        <Fraction 
                          numerator="0.8 × 0.15" 
                          denominator="0.8 × 0.15 + 0.2 × 0.85" 
                        />
                        <span className="ml-2">≈ 0.41 or 41%</span>
                      </div>
                    </div>

                    {/* Mobile version with plain text - hidden on desktop */}
                    <div className="sm:hidden space-y-2 text-gray-900 text-sm font-medium">
                      <p>P(Blue|Witness) = P(Witness|Blue) × P(Blue) / P(Witness)</p>
                      <p>= (0.8 × 0.15) / (0.8 × 0.15 + 0.2 × 0.85)</p>
                      <p>≈ 0.41 or 41%</p>
                    </div>
                  </div>
                  
                  <Text className="text-gray-700 text-sm mt-2">
                    To avoid this fallacy, always consider the base rate (prior probability) when evaluating new evidence. 
                    Bayes' theorem provides the mathematical framework for correctly updating probabilities based on new information.
                  </Text>
                </div>
              </div>
              
              {/* Step 2: Connection to the Bias */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to the Base Rate Fallacy
                </Text>
                <Text className="text-gray-700">
                  The base rate fallacy occurs when we ignore general statistical information (base rates) and focus too much on specific details, 
                  leading to incorrect probability judgments.
                </Text>
                
                <Text className="text-gray-700">
                  In this case, most people answer 80% (the witness reliability) because they focus on the specific information about the witness 
                  and ignore the base rate that only 15% of taxis are Blue. This is a classic example of the base rate fallacy.
                </Text>
              </div>
              
              {/* Step 3: Study Findings */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <Text className="text-gray-700">
                  The Taxi Cab Problem was introduced by Kahneman and Tversky in their groundbreaking research on cognitive biases in the 1970s. 
                  In their studies, most participants estimated the probability to be around 80%, focusing on the witness reliability 
                  and ignoring the base rate of Blue taxis.
                </Text>
                
                <Text className="text-gray-700">
                  This problem has been replicated many times, even with professionals like doctors, lawyers, and statisticians, 
                  showing how pervasive this cognitive bias is.
                </Text>

                <Text className="text-gray-700 mt-1">
                  Probability the taxi was Blue given the witness said Blue: {getChartData().probabilityGivenWitness.toFixed(1)}%
                </Text>
                
                <Text className="text-gray-700">
                  This is much lower than the witness reliability (80%) because the base rate of Blue taxis is so low (15%).
                </Text>

                <Text className="text-gray-700 mt-1">
                  The charts below show the distribution of taxis and witness testimony outcomes, illustrating why witness testimony alone isn't sufficient evidence.
                </Text>
                
                {/* Base Rate Chart */}
                <GraphContainer>
                  <Text className="text-center text-base font-medium mb-2">
                    Base Rate: Distribution of Taxis in the City
                  </Text>
                  <div className="flex flex-col md:flex-row md:justify-center md:items-center h-[350px] md:h-[300px]">
                    <div className="flex-1 md:flex-initial md:w-[300px] h-[250px] md:h-full" style={{ pointerEvents: 'none' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                          {/* Tooltip disabled to prevent interaction */}
                          <Pie
                            data={getChartData().taxiDistribution}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            isAnimationActive={false}
                            style={{ pointerEvents: 'none' }}
                            label={({ name, value, x, y, cx, cy }) => {
                              return (
                                <g className="hidden md:block">
                                  <text 
                                    x={x} 
                                    y={y} 
                                    fill="#000000" 
                                    fontSize={12} 
                                    textAnchor={x > cx ? 'start' : 'end'} 
                                    dominantBaseline="middle"
                                  >
                                    {`${value.toFixed(1)}%`}
                                  </text>
                                </g>
                              );
                            }}
                            labelLine={false}
                          >
                            {getChartData().taxiDistribution.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={COLORS[index % COLORS.length]} 
                                style={{ pointerEvents: 'none' }}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="md:w-[200px] px-4 py-2 bg-gray-50 rounded-lg border border-gray-100 mt-2 md:mt-0 md:ml-4 flex-shrink-0">
                      {getLegendItems(getChartData().taxiDistribution)}
                    </div>
                  </div>
                </GraphContainer>
                
                {/* Witness Outcomes Chart */}
                <GraphContainer>
                  <Text className="text-center text-base font-medium mb-2">
                    Witness Testimony Outcomes
                  </Text>
                  <div className="flex flex-col md:flex-row md:justify-center md:items-center h-[350px] md:h-[300px]">
                    <div className="flex-1 md:flex-initial md:w-[300px] h-[250px] md:h-full" style={{ pointerEvents: 'none' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                          {/* Tooltip disabled to prevent interaction */}
                          <Pie
                            data={getChartData().witnessOutcomes}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            isAnimationActive={false}
                            style={{ pointerEvents: 'none' }}
                            label={({ name, value, x, y, cx, cy }) => {
                              return (
                                <g className="hidden md:block">
                                  <text 
                                    x={x} 
                                    y={y} 
                                    fill="#000000" 
                                    fontSize={12} 
                                    textAnchor={x > cx ? 'start' : 'end'} 
                                    dominantBaseline="middle"
                                  >
                                    {`${value.toFixed(1)}%`}
                                  </text>
                                </g>
                              );
                            }}
                            labelLine={false}
                          >
                            {getChartData().witnessOutcomes.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={COLORS[index % COLORS.length]} 
                                style={{ pointerEvents: 'none' }}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="md:w-[200px] px-4 py-2 bg-gray-50 rounded-lg border border-gray-100 mt-2 md:mt-0 md:ml-4 flex-shrink-0">
                      {getLegendItems(getChartData().witnessOutcomes, true)}
                    </div>
                  </div>
                </GraphContainer>
              </div>
            </div>
          </ResultBlock>

          {/* About the Study Block */}
          <ResultBlock
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by Kahneman and Tversky (1973), "On the Psychology of Prediction," published in Psychological Review. This influential study demonstrated how people neglect base rates when making probabilistic judgments, leading to systematic errors in prediction and decision making.
              </Text>
              
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          {/* Try Again Button */}
          <div className="flex justify-center">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <SimulationTemplate
      title="Base Rate Fallacy Simulation"
      description="Experience how we tend to ignore general statistical information (base rates) and focus too much on specific details, leading to incorrect probability judgments."
    >
      {!started ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => {
            setStarted(true);
            setStage('task1');
          }}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default BaseRateFallacySimulation; 